import { ref, uploadBytes, getDownloadURL, deleteObject, StorageReference } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { storage, auth } from './config';
import { FacilityAssessment, FacilityPhoto, FacilityPlan, SystemKey } from '../models/types';
import { isPdfFile, rasterizePdf } from '../utils/pdfRaster';

/* The orgId claim only appears in the ID token after a refresh — a user who just created an
   org or accepted an invite is still holding a token minted before syncOrgClaims ran, and
   Storage would deny them for up to an hour. Rather than make the caller reason about token
   lifetimes, treat one storage/unauthorized as "possibly stale claim": force a refresh and
   retry once. A genuine permission failure just fails again and surfaces normally. */
async function uploadWithClaimRefresh(
  storageRef: StorageReference, blob: Blob, contentType: string,
): Promise<void> {
  try {
    await uploadBytes(storageRef, blob, { contentType });
  } catch (err: any) {
    if (err?.code !== 'storage/unauthorized' || !auth.currentUser) throw err;
    await auth.currentUser.getIdToken(true);
    try {
      await uploadBytes(storageRef, blob, { contentType });
    } catch (retryErr: any) {
      /* Still denied with a freshly minted token, so the claim genuinely doesn't match the path.
         Attach both values — otherwise this is indistinguishable from a stale token and the only
         way to tell them apart is server-side archaeology. */
      const claims = await auth.currentUser.getIdTokenResult()
        .then(r => r.claims as { orgId?: string })
        .catch(() => ({} as { orgId?: string }));
      throw Object.assign(retryErr, {
        tokenOrgId: claims.orgId ?? null,
        attemptedPath: storageRef.fullPath,
      });
    }
  }
}

/* Downscale camera photos before upload — nameplate shots from phones are 5–10 MB;
   1600px JPEG is plenty for reports and keeps Storage costs down. */
const downscaleImage = (file: File, maxDim = 1600, quality = 0.8): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas unavailable')); return; }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error('Image encode failed')),
        'image/jpeg', quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Not a readable image')); };
    img.src = url;
  });

/* Firebase storage errors surface as opaque codes; map the ones a field tech or admin can
   actually act on. Keeps the code in the message so support requests are diagnosable. */
export function storageErrorMessage(err: any, action = 'Upload'): string {
  const code = err?.code as string | undefined;
  switch (code) {
    case 'storage/unauthorized': {
      // Reached only after uploadWithClaimRefresh already retried with a fresh token, so a
      // stale orgId claim has been ruled out by this point. Report the two values that have to
      // agree, so the cause is readable from the message instead of needing a server-side dig.
      const tokenOrg = 'tokenOrgId' in (err ?? {}) ? err.tokenOrgId : undefined;
      const pathOrg = typeof err?.attemptedPath === 'string' ? err.attemptedPath.split('/')[1] : undefined;
      const detail = tokenOrg === undefined ? ''
        : tokenOrg === null
          ? ' Your sign-in carries no organization yet — open Team to confirm your membership, then sign out and back in.'
          : tokenOrg === pathOrg
            ? ` Your organization (${tokenOrg}) matches this file's path, so the Storage rules themselves are rejecting it.`
            : ` Your sign-in is for organization ${tokenOrg} but this project stores files under ${pathOrg}.`;
      return `${action} denied by Storage rules (storage/unauthorized).${detail}`;
    }
    case 'app/no-org':
      return `${action} failed — no organization on your profile. Reload the page; if it persists, ` +
        `re-open Team to confirm your membership.`;
    case 'storage/unauthenticated':
      return `${action} failed — your session expired (storage/unauthenticated). Sign in again.`;
    case 'storage/retry-limit-exceeded':
      return `${action} timed out (storage/retry-limit-exceeded) — connection too slow or dropped. Retry.`;
    case 'storage/quota-exceeded':
      return `${action} failed — Storage quota exceeded (storage/quota-exceeded).`;
    case 'storage/canceled':
      return `${action} canceled.`;
    default: break;
  }
  // Client-side decode failures from downscaleImage (e.g. iPhone HEIC in a desktop browser).
  if (err?.message === 'Not a readable image')
    return `${action} failed — that file isn't an image this browser can read. ` +
      `iPhone HEIC photos need to be shared as JPEG, or taken with the in-app camera button.`;
  if (err?.message === 'Image encode failed')
    return `${action} failed while re-encoding the image. Try a different photo.`;
  return `${action} failed${code ? ` (${code})` : ''}${err?.message ? `: ${err.message}` : '.'}`;
}

/* Uploads are keyed by org because storage.rules authorizes from the orgId custom claim and
   can't look up which org owns a projectId. Callers pass orgId from useOrg(); a missing one is
   a bug (PrivateRoute forces /setup before the app is usable) and fails loudly here rather
   than writing to a path the rules will reject. */
function orgPrefix(orgId: string | null | undefined, projectId: string): string {
  if (!orgId) throw Object.assign(new Error('No organization on your profile — reload and try again.'), { code: 'app/no-org' });
  return `orgs/${orgId}/projects/${projectId}`;
}

/* Downscale a captured file to the JPEG blob we actually store. Exposed so the offline queue can
   do the (CPU) resize at capture time and stash a small blob, then upload it later unchanged. */
export function prepareFacilityPhoto(file: File): Promise<Blob> {
  return downscaleImage(file);
}

/* Upload an already-prepared photo JPEG. Split out from uploadFacilityPhoto so the offline queue
   can replay a stored blob on reconnect via the same path. */
export async function uploadFacilityPhotoBlob(
  orgId: string | null, projectId: string, assessmentId: string, assetId: string, blob: Blob,
): Promise<FacilityPhoto> {
  const id = uuidv4();
  const storagePath = `${orgPrefix(orgId, projectId)}/facility/${assessmentId}/${assetId}/${id}.jpg`;
  const storageRef = ref(storage, storagePath);
  await uploadWithClaimRefresh(storageRef, blob, 'image/jpeg');
  const url = await getDownloadURL(storageRef);
  return { id, url, storagePath, takenAt: new Date().toISOString() };
}

export async function uploadFacilityPhoto(
  orgId: string | null, projectId: string, assessmentId: string, assetId: string, file: File,
): Promise<FacilityPhoto> {
  return uploadFacilityPhotoBlob(orgId, projectId, assessmentId, assetId, await downscaleImage(file));
}

/* Uploads one already-prepared JPEG (a downscaled image, or a rasterized PDF page) as a plan.
   Floor plans keep more resolution than photos — techs zoom into room numbers. */
async function putPlanJpeg(
  orgId: string | null, projectId: string, assessmentId: string, blob: Blob, name: string,
): Promise<FacilityPlan> {
  const id = uuidv4();
  const storagePath = `${orgPrefix(orgId, projectId)}/facility/${assessmentId}/plans/${id}.jpg`;
  const storageRef = ref(storage, storagePath);
  await uploadWithClaimRefresh(storageRef, blob, 'image/jpeg');
  const url = await getDownloadURL(storageRef);
  return { id, name, url, storagePath, uploadedAt: new Date().toISOString() };
}

/* A plan upload is either a raster image (one plan) or a PDF drawing set (one plan per page/sheet).
   Both land as JPEGs so the viewer, pins and storage stay on a single path — the durham-om model,
   where each "sheet" is just another selectable raster plan (Path A). PDFs are rasterized in the
   browser at upload time; `onProgress` fires per page so a big set can show its progress. */
export async function uploadFacilityPlanFile(
  orgId: string | null, projectId: string, assessmentId: string, file: File,
  onProgress?: (done: number, total: number) => void,
): Promise<FacilityPlan[]> {
  const baseName = file.name.replace(/\.[^.]+$/, '') || 'Plan';

  if (isPdfFile(file)) {
    const plans: FacilityPlan[] = [];
    await rasterizePdf(file, async ({ pageNumber, blob }, total) => {
      const name = total > 1 ? `${baseName} — Sheet ${pageNumber}` : baseName;
      plans.push(await putPlanJpeg(orgId, projectId, assessmentId, blob, name));
      onProgress?.(pageNumber, total);
    });
    return plans;
  }

  const blob = await downscaleImage(file, 2800, 0.85);
  const plan = await putPlanJpeg(orgId, projectId, assessmentId, blob, baseName);
  onProgress?.(1, 1);
  return [plan];
}

/* A single identifying photo for the project itself — e.g. a labeled aerial/site shot showing
   which building an assessment covers — shown on the detailed report's cover. Fixed filename
   (not uuid'd like facility photos) so re-uploading replaces it in place rather than
   accumulating orphans; storage.rules gives this path its own match, separate from facility/. */
export async function uploadProjectSitePhoto(
  orgId: string | null, projectId: string, file: File,
): Promise<string> {
  const blob = await downscaleImage(file, 2000, 0.85);
  const storageRef = ref(storage, `${orgPrefix(orgId, projectId)}/sitePhoto.jpg`);
  await uploadWithClaimRefresh(storageRef, blob, 'image/jpeg');
  return getDownloadURL(storageRef);
}

export async function deleteProjectSitePhoto(orgId: string | null, projectId: string): Promise<void> {
  await deleteStorageFile(`${orgPrefix(orgId, projectId)}/sitePhoto.jpg`);
}

export async function deleteStorageFile(storagePath: string): Promise<void> {
  try {
    await deleteObject(ref(storage, storagePath));
  } catch (err: any) {
    if (err?.code !== 'storage/object-not-found') throw err;
  }
}

/* Back-compat alias for photo deletion call sites. */
export const deleteFacilityPhoto = deleteStorageFile;

/* Best-effort bulk delete for the files an assessment or asset owns. Deleting a Firestore document
   never touches Storage, so an assessment's photos and plan sheets used to outlive it indefinitely
   — still fetchable by anyone holding an old download URL, and billed. Every path is attempted
   (allSettled, not all) so one failure can't strand the rest; the count of failures comes back so
   the caller can say so. Not-found is not a failure (deleteStorageFile already treats it as done). */
export async function deleteStorageFiles(storagePaths: string[]): Promise<{ failed: number }> {
  const results = await Promise.allSettled(storagePaths.map(p => deleteStorageFile(p)));
  const failed = results.filter(r => r.status === 'rejected').length;
  if (failed) console.warn(`Storage cleanup: ${failed} of ${storagePaths.length} file(s) could not be deleted`);
  return { failed };
}

/* Every Storage path an assessment owns: each asset's photos plus its plan sheets. */
export function assessmentStoragePaths(a: FacilityAssessment): string[] {
  const systems: SystemKey[] = ['controls', 'electrical', 'mechanical', 'plumbing'];
  return [
    ...systems.flatMap(s => a[s].assets.flatMap(asset => (asset.photos ?? []).map(p => p.storagePath))),
    ...(a.plans ?? []).map(p => p.storagePath),
  ].filter((p): p is string => !!p);
}
